import { useNavigate } from "react-router";
import { ShellContent } from "../../shell/Shell.jsx";
import tokens from "../../../design-system/tokens.js";
import Layout from "../../../design-system/components/Layout/Layout.jsx";
import { SanityImg } from "../../../lib/images.jsx";
import BlockContent from "../../../design-system/components/BlockContent/BlockContent.jsx";
import Button from "../../../design-system/components/Button/Button.jsx";
import { StepDuration } from "./components/StepDuration/StepDuration.jsx";
import styles from "./SparkStepOverviewWithData.css.ts";
import { SparkStepHeader } from "./components/SparkStepHeader/SparkStepHeader.jsx";
import { StepNavigation } from "./components/StepNavigation/StepNavigation.jsx";

/**
 * SparkStepWithData is a React component that represents a step in a workshop module.
 *
 * @param {Object} step - The current step data.
 * @param {Object} content - Content associated with the step.
 * @param {Array} stationSteps - Array of steps for the station.
 * @param {Function} nextStep - Function to navigate to the next step.
 * @param {Function} prevStep - Function to navigate to the previous step.
 * @param {Object} spark - Additional data or configurations necessary for rendering step content.
 * @returns {JSX.Element} A component that renders the step content including navigation, images, and descriptive information.
 */
export const SparkStepOverviewWithData = ({
  step,
  content = null,
  stationSteps = null,
  nextStep = null,
  prevStep = null,
  stageName = null,
  disableSome = false,
}) => {
  const navigate = useNavigate();
  const clickHandlerPrevStep = () => {
    const path = prevStep ? `../step/${prevStep.slug}` : "..";
    navigate(path);
  };
  const clickHandlerNextStep = () => {
    const path = nextStep ? `../step/${nextStep.slug}` : "..";
    navigate(path);
  };

  return (
    <ShellContent bg={tokens.color.full.gray100} theme="light">
      {/* Remount on step change so the scrolling container in `Layout` starts back at the top. */}
      <Layout
        key={step.slug}
        Header={() => (
          <SparkStepHeader
            moduleTitle={step.content.station?.title}
            chapterTitle={stageName}
            StepNavigation={
              <StepNavigation
                step={step}
                prevStep={prevStep}
                nextStep={nextStep}
                stationSteps={stationSteps}
                id="step-navigation"
                disableSome={disableSome}
              />
            }
          />
        )}
      >
        <div className={styles.stepContentContainer}>
          <div className={styles.stepDurationArea}>
            <StepDuration step={step} stationSteps={stationSteps} />

            {step.content.typeDarkImage && (
              <SanityImg
                url={step.content.typeDarkImage}
                height={30}
                className={styles.typeDarkImage}
              />
            )}
          </div>
          <h3 className={styles.stepTitle}>{step.title}</h3>
          {content && <BlockContent content={content} />}
          <div className={styles.buttonContainer}>
            <Button
              width="fit-content"
              variant="secondary"
              onClick={clickHandlerPrevStep}
            >
              Zurück
            </Button>

            <Button width="fit-content" onClick={clickHandlerNextStep}>
              Weiter
            </Button>
          </div>
        </div>
      </Layout>
    </ShellContent>
  );
};
